import React, { createContext, useContext, useState } from 'react';
import {
  ApiComponent,
  ApiHotel,
  ApiHotelRoom,
  ApiItemType,
  ApiRentalCarOffice,
  ApiTraveler
} from '@ibe/api';
import { SessionStorage } from '@ibe/services';
import { CollapsibleWizardItemType } from '../CollapsibleWizard/ContextAndProvider';
import CheckoutStore from '../../../../Pages/Checkout/CheckoutStore';
import { useBookingService } from '@ibe/components';
import { extendedBookingSessionKey, eCommerceSessionKey } from '../../../../Config/sessionkeys';
import dayjs from 'dayjs';

export interface ExtendedApiTraveler extends ApiTraveler {
  position?: number;
}

export interface SelectedItemsType {
  duration?: {
    mainComponent: ApiComponent;
    selectedExtensionComponent?: ApiComponent;
    extensionDuration: number;
    // The initial package provides more precise date information than the hotel,
    // because flights can be overnight and we can't get that info from the hotel .
    // That's why I rely on the initial package date to handle the date.
    mainComponentDuration?: {
      startDate?: string;
      endDate?: string;
    };
  };
  hotelMain?: ApiHotel;
  travelers?: ApiTraveler[];
  busStation?: ApiRentalCarOffice;
  cateringMain?: {
    mealTypeCode: string;
    mealTypeDescription: string;
    rateId: string;
  };

  hotelOptional?: ApiHotel;
  cateringOptional?: {
    mealTypeCode: string;
    mealTypeDescription: string;
    rateId: string;
  };

  roomsMain?: SelectedRoom[];

  roomsOptional?: SelectedRoom[];

  steps?: Omit<CollapsibleWizardItemType, 'component'>[];

  hotelComponents?: ApiComponent[];
}

export interface SelectedRoom {
  room: ApiHotelRoom;
  travelers: ExtendedApiTraveler[];
}

interface ExtendedBookingContextProps {
  isLoading: boolean;
  setIsLoading: (a: boolean) => void;
  store: CheckoutStore;
  selectedItems: SelectedItemsType;
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedItemsType>>;
}

const ExtendedBookingContext = createContext<ExtendedBookingContextProps | null>(null);

export const useExtendedBooking = () => {
  const context = useContext(ExtendedBookingContext);
  if (!context) {
    throw new Error('useExtendedBooking must be used within an ExtendedBookingProvider');
  }
  return context;
};
export const useExtendedBookingForPriceOverview = () => {
  const context = useContext(ExtendedBookingContext);
  return context ? context.selectedItems : null;
};
export const ExtendedBookingProvider: React.FC<{
  children: React.ReactNode;
  store: CheckoutStore;
}> = ({ children, store }) => {
  const booking = useBookingService();
  const bookedPackage = booking?.bookedItems?.find(it => it.itemType === ApiItemType.PACKAGE);

  const packageDetails = booking?.packageCart?.packageModel?.packageDetails ?? [];
  const inBoundComponents = packageDetails
    .flatMap(detail => detail.components)
    .find(comp => comp.itemType === ApiItemType.FLIGHT && comp.subItemType === ApiItemType.INBOUND);
  const inboundFlyMatchCode = inBoundComponents?.selectedItems?.[0]?.matchCode;
  let endDate = bookedPackage?.endDate;

  if (inboundFlyMatchCode) {
    const extensionHotel = packageDetails
      .flatMap(detail => detail.components)
      .filter(
        comp =>
          comp.itemType === ApiItemType.HOTEL &&
          comp.selectableItems.find(item => item.matchCode === inboundFlyMatchCode)
      )
      .flatMap(comp => comp.selectableItems)
      .find(item => item.matchCode === inboundFlyMatchCode) as ApiHotel;

    if (endDate && extensionHotel) {
      endDate = dayjs(endDate, 'YYYY-MM-DD')
        .subtract(extensionHotel?.durationInfo?.duration ?? 0, 'day')
        .format('YYYY-MM-DD');
    }
  }

  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItemsType>(
    extendedBookingStorage.get() ??
      ({
        duration: {
          mainComponentDuration: {
            startDate: bookedPackage?.startDate,
            endDate
          }
        }
      } as SelectedItemsType)
  );

  return (
    <ExtendedBookingContext.Provider
      value={{
        isLoading,
        setIsLoading,
        selectedItems,
        setSelectedItems,
        store
      }}
    >
      {children}
    </ExtendedBookingContext.Provider>
  );
};

export const extendedBookingStorage = new SessionStorage<SelectedItemsType>(
  extendedBookingSessionKey
);

export const eCommerceStorage = new SessionStorage<Ecommerce>(eCommerceSessionKey);
